import React, { useCallback, useMemo } from "react";
import { StyleSheet, useWindowDimensions } from "react-native";

import {
  isWeb,
  match,
  useGetAverageReviewsForApplicationQuery,
  useListHomepageReviewsQuery,
} from "@stepapp/core";
import {
  Button,
  TestimonialItem,
  TestimonialPlaceholder,
  palette,
  spacing,
  useBreakpoints,
} from "@stepapp/ui";
import Block from "@stepapp/ui/src/components/Block";
import Rating from "@stepapp/ui/src/components/Rating";

import { useRouter } from "expo-router";
import { useTranslation } from "react-i18next";
import { AppStorage } from "../../services/AppStorage";

const Testimonials = () => {
  const { t } = useTranslation();
  const { md } = useBreakpoints();
  const { width: screenWidth } = useWindowDimensions();
  const { navigate } = useRouter();
  const { data, loading } = useListHomepageReviewsQuery();
  const countryCode = AppStorage().get("countryCode");

  const width = md ? "31%" : "100%";
  const { data: avgReviews, loading: loadingAvgReviews } =
    useGetAverageReviewsForApplicationQuery();
  const filteredRatings = useMemo(
    () =>
      data?.listReviewsForHomepage
        ?.filter(
          ({ comment, customerName }) =>
            !!comment && !customerName.includes("test") && !comment.includes("test"),
        )
        .slice(0, 6),
    [data],
  );

  const showPlaceholder = loading && !data?.listReviewsForHomepage;
  const testimonialPlaceholderWidth = md
    ? (screenWidth > 1160 ? 1160 : screenWidth - 24) * 0.31
    : screenWidth - 40;

  const showMoreReviews = match(countryCode)
    .with("PL", () => true)
    .with("NO", () => false)
    .otherwise(() => true);

  const handleOpenReviewsPage = useCallback(() => {
    navigate("/reviews");
  }, [navigate]);

  return (
    <Block marginBottom={isWeb ? 0 : 26}>
      <Rating
        loading={loadingAvgReviews}
        numberOfReviews={avgReviews?.getAverageReviewsForApplication.total}
        rating={avgReviews?.getAverageReviewsForApplication.average}
        openReviews={handleOpenReviewsPage}
        t={t}
      />

      <Block justifySpace row wrap flex={1} marginTop={3}>
        {showPlaceholder &&
          [...new Array(6).keys()].map((index) => (
            <TestimonialPlaceholder
              key={index}
              width={Math.round(testimonialPlaceholderWidth / 8)}
            />
          ))}

        {filteredRatings?.map((review) => (
          <TestimonialItem
            containerStyle={[styles.testimonial, { width }]}
            key={`${review?.id}`}
            review={review}
            postedAt={review?.postedAt}
            countryCode={countryCode}
            openReviews={handleOpenReviewsPage}
            t={t}
          />
        ))}
      </Block>
      {showMoreReviews && (
        <Button
          type="link"
          label={t("screens.dashboard.seeAllReviews")}
          onPress={handleOpenReviewsPage}
          textStyle={{ color: palette.common.black }}
          style={styles.linkButton}
        />
      )}
    </Block>
  );
};

export default React.memo(Testimonials);

const styles = StyleSheet.create({
  testimonial: {
    marginBottom: spacing.unit * 4,
  },
  linkButton: {
    alignItems: "flex-end",
    marginTop: 12,
  },
});
